<section class="rq">
  <!-- ── En-tête ───────────────────────────────────────────── -->
  <header class="rq__header">
    <div class="rq__heading">
      <h2 class="rq__title">
        <i class="fa-solid fa-clipboard-check rq__title-icon" aria-hidden="true"></i>
        {{ 'REVIEW_QUEUE.TITLE' | translate }}
      </h2>
      <p class="rq__subtitle">{{ 'REVIEW_QUEUE.SUBTITLE' | translate }}</p>
    </div>

    <div class="rq__header-actions">
      @if (!loading() && !loadError()) {
        <span class="rq__count">
          <i class="fa-regular fa-hourglass-half" aria-hidden="true"></i>
          {{ 'REVIEW_QUEUE.PENDING_COUNT' | translate: { count: total() } }}
        </span>
      }
      <!-- Valide toute la file en une requête : un appel par question
           déclencherait l'anti-flood du back. -->
      @if (!loading() && !loadError() && total() > 0) {
        <button
          type="button"
          class="rq__approve-all"
          [disabled]="deciding()"
          (click)="onApproveAll()"
        >
          <i class="fa-solid fa-check-double" aria-hidden="true"></i>
          {{ 'REVIEW_QUEUE.APPROVE_ALL' | translate }}
        </button>
      }
      <button
        type="button"
        class="rq__refresh"
        [disabled]="loading()"
        (click)="load()"
      >
        <i class="fa-solid fa-rotate" aria-hidden="true"></i>
        {{ 'REVIEW_QUEUE.REFRESH' | translate }}
      </button>
    </div>
  </header>

  <!-- Aide-mémoire des raccourcis : l'écran se traite au clavier. -->
  <p class="rq__shortcuts">
    <kbd>A</kbd> {{ 'REVIEW_QUEUE.SHORTCUT_APPROVE' | translate }}
    <span class="rq__shortcuts-sep">·</span>
    <kbd>R</kbd> {{ 'REVIEW_QUEUE.SHORTCUT_REJECT' | translate }}
    <span class="rq__shortcuts-sep">·</span>
    <kbd>J</kbd><kbd>K</kbd> {{ 'REVIEW_QUEUE.SHORTCUT_NAVIGATE' | translate }}
  </p>

  <!-- ── États ─────────────────────────────────────────────── -->
  @if (loading()) {
    <div class="rq__loading" role="status">
      <span class="spinner-border" aria-hidden="true"></span>
      <p class="rq__loading-text">{{ 'REVIEW_QUEUE.LOADING' | translate }}</p>
    </div>
  } @else if (loadError()) {
    <app-error-state
      titleKey="REVIEW_QUEUE.ERROR_TITLE"
      messageKey="REVIEW_QUEUE.ERROR_MESSAGE"
      [detail]="loadError() ?? ''"
      (retry)="load()"
    />
  } @else if (rows().length === 0) {
    <!-- Une file vide est une bonne nouvelle : tout a été relu. -->
    <app-empty-state
      icon="check-circle"
      titleKey="REVIEW_QUEUE.EMPTY_TITLE"
      messageKey="REVIEW_QUEUE.EMPTY_MESSAGE"
    />
  } @else {
    <div class="rq__layout">
      <!-- ── Volet gauche : la file ───────────────────────── -->
      <aside
        class="rq__queue"
        [attr.aria-label]="'REVIEW_QUEUE.LIST_LABEL' | translate"
      >
        <ul class="rq__list">
          @for (row of rows(); track row.item.id) {
            <li>
              <button
                type="button"
                [id]="'rq-item-' + row.item.id"
                class="rq__item"
                [class.rq__item--active]="row.item.id === selectedId()"
                [class.rq__item--ai]="row.isAiGenerated"
                [attr.aria-current]="row.item.id === selectedId() ? 'true' : null"
                (click)="select(row.item)"
              >
                @if (row.isAiGenerated) {
                  <span class="rq__ai-flag">
                    <i class="fa-solid fa-robot" aria-hidden="true"></i>
                    {{ 'REVIEW_QUEUE.ORIGIN_AI_GENERATED' | translate }}
                  </span>
                }

                <span class="rq__item-title"><app-math-text [text]="row.item.title" [inline]="true"></app-math-text></span>

                <span class="rq__item-meta">
                  @if (row.item.theme?.title; as themeTitle) {
                    <span class="rq__chip rq__chip--theme">
                      <i class="fa-solid fa-layer-group" aria-hidden="true"></i>
                      {{ themeTitle }}
                    </span>
                  }

                  <span class="rq__chip">
                    @if (typeKey(row.item.questionType); as key) {
                      {{ key | translate }}
                    } @else {
                      {{ row.item.questionType }}
                    }
                  </span>

                  <span
                    class="rq__chip rq__chip--difficulty"
                    [attr.data-level]="row.item.difficultyLevel"
                  >
                    @if (difficultyKey(row.item.difficultyLevel); as key) {
                      {{ key | translate }}
                    } @else {
                      {{ row.item.difficultyLevel }}
                    }
                  </span>

                  @if (!row.isAiGenerated) {
                    <span class="rq__chip">
                      @if (originKey(row.item.origin); as key) {
                        {{ key | translate }}
                      } @else {
                        {{ row.item.origin }}
                      }
                    </span>
                  }
                </span>

                @if (row.ageDays !== null) {
                  <span class="rq__item-age">
                    <i class="fa-regular fa-clock" aria-hidden="true"></i>
                    @if (row.ageDays === 0) {
                      {{ 'REVIEW_QUEUE.AGE_TODAY' | translate }}
                    } @else {
                      {{ 'REVIEW_QUEUE.AGE_DAYS' | translate: { days: row.ageDays } }}
                    }
                  </span>
                }
              </button>
            </li>
          }
        </ul>
      </aside>

      <!-- ── Volet droit : la question sélectionnée ───────── -->
      <div class="rq__detail">
        @if (selected(); as q) {
          <article class="rq__preview" [attr.dir]="q.direction">
            <header class="rq__preview-head">
              <div class="rq__badges">
                <span class="rq__badge rq__badge--type">
                  @if (typeKey(q.questionType); as key) {
                    {{ key | translate }}
                  } @else {
                    {{ q.questionType }}
                  }
                </span>

                <span
                  class="rq__badge rq__badge--difficulty"
                  [attr.data-level]="q.difficultyLevel"
                >
                  @if (difficultyKey(q.difficultyLevel); as key) {
                    {{ key | translate }}
                  } @else {
                    {{ q.difficultyLevel }}
                  }
                </span>

                <span class="rq__badge">
                  {{ 'REVIEW_QUEUE.POINTS' | translate: { points: q.points } }}
                </span>

                @if (q.timeLimitSeconds) {
                  <span class="rq__badge">
                    {{ 'REVIEW_QUEUE.TIME_LIMIT' | translate: { seconds: q.timeLimitSeconds } }}
                  </span>
                }

                <span class="rq__badge">{{ q.language }}</span>

                <span class="rq__badge">
                  {{ 'REVIEW_QUEUE.VERSION' | translate: { version: q.version } }}
                </span>

                <span
                  class="rq__badge"
                  [class.rq__badge--ai]="q.origin === 'ai_generated'"
                >
                  @if (q.origin === 'ai_generated') {
                    <i class="fa-solid fa-robot" aria-hidden="true"></i>
                  }
                  @if (originKey(q.origin); as key) {
                    {{ key | translate }}
                  } @else {
                    {{ q.origin }}
                  }
                </span>
              </div>

              @if (q.origin === 'ai_generated') {
                <p class="rq__ai-warning">
                  <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                  {{ 'REVIEW_QUEUE.AI_WARNING' | translate }}
                </p>
              }
            </header>

            <!-- Rendu tel que l'élève le verra, solution mise en évidence -->
            <h3 class="rq__statement"><app-math-text [text]="q.title"></app-math-text></h3>

            @if (q.description) {
              <p class="rq__description"><app-math-text [text]="q.description"></app-math-text></p>
            }

            <div class="rq__options">
              @if (optionsUnavailable()) {
                <p class="rq__options-status rq__options-status--missing">
                  <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                  {{ 'REVIEW_QUEUE.OPTIONS_UNAVAILABLE' | translate }}
                </p>
              } @else {
                <ol class="rq__option-list">
                  @for (option of sortedOptions(); track option.id; let i = $index) {
                    <li
                      class="rq__option"
                      [class.rq__option--correct]="option.isCorrect"
                    >
                      <span class="rq__option-letter">{{ optionLetter(i) }}</span>
                      <span class="rq__option-content"><app-math-text [text]="option.content"></app-math-text></span>
                      @if (option.isCorrect) {
                        <span class="rq__option-correct">
                          <i class="fa-solid fa-check" aria-hidden="true"></i>
                          {{ 'REVIEW_QUEUE.CORRECT_ANSWER' | translate }}
                        </span>
                      }
                    </li>
                  }
                </ol>
              }
            </div>

            <dl class="rq__facts">
              @if (q.explanation) {
                <dt>{{ 'REVIEW_QUEUE.EXPLANATION' | translate }}</dt>
                <dd><app-math-text [text]="q.explanation"></app-math-text></dd>
              }

              @if (q.hints) {
                <dt>{{ 'REVIEW_QUEUE.HINTS' | translate }}</dt>
                <dd><app-math-text [text]="q.hints"></app-math-text></dd>
              }

              @if (q.theme?.title; as themeTitle) {
                <dt>{{ 'REVIEW_QUEUE.THEME' | translate }}</dt>
                <dd>{{ themeTitle }}</dd>
              }

              <!-- Ce qui RANGE la question : sans ces lignes, la relecture
                   approuvait un enonce sans voir sous quelle competence il
                   allait tomber. Les libelles sont ceux du choix des colonnes
                   a l'import : le relecteur y retrouve les mots du document. -->
              @for (d of descripteurs(); track d.labelKey) {
                <dt>{{ d.labelKey | translate }}</dt>
                <dd>{{ d.value }}</dd>
              }

              @if (q.tags.length > 0) {
                <dt>{{ 'REVIEW_QUEUE.TAGS' | translate }}</dt>
                <dd>
                  <span class="rq__tags">
                    @for (tag of q.tags; track tag) {
                      <span class="rq__tag">{{ tag }}</span>
                    }
                  </span>
                </dd>
              }
            </dl>
          </article>

          <!-- D'ou vient la question, et ce que le document disait si l'ecran
               de controle l'a corrigee avant l'import. -->
          <app-question-source [source]="q.source ?? null" />

          <div class="rq__actions-edit">
            <a
              class="rq__edit"
              [routerLink]="['/admin', 'questions', q.id, 'edit']"
              [queryParams]="retourVersLaFile"
            >
              <i class="fa-solid fa-pen" aria-hidden="true"></i>
              {{ 'REVIEW_QUEUE.EDIT' | translate }}
            </a>
            <span class="rq__edit-note">{{ 'REVIEW_QUEUE.EDIT_NOTE' | translate }}</span>
          </div>

          <app-review-decision
            [question]="q"
            [submitting]="deciding()"
            (approve)="onApprove($event)"
            (reject)="onReject($event)"
          />

          <app-review-history
            [reviews]="history()"
            [loading]="historyLoading()"
            [errored]="historyErrored()"
            (retry)="reloadHistory()"
          />
        }
      </div>
    </div>
  }
</section>
